/**
 * CrossProjectDependencies Component
 * AIDLC Administrative Backend
 *
 * Displays cross-project blockers and dependencies in a table view.
 */

import { useState, useEffect } from 'react';
import { fetchCrossProjectBlockers } from '../../../services/blockerService';
import { Badge } from '../../shared/Badge';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import type { Blocker } from '../../../types/projects';

const severityVariants: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'neutral',
};

function getAgentLabel(agentId: string | null): string {
  if (!agentId) return '--';
  return agentId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function CrossProjectDependencies() {
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchCrossProjectBlockers();
        if (!cancelled) setBlockers(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load cross-project blockers');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <LoadingSpinner message="Loading dependencies..." size="sm" />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <p className="text-red-700 text-xs">{error}</p>
      </div>
    );
  }

  if (blockers.length === 0) {
    return (
      <EmptyState
        title="No cross-project dependencies"
        description="All projects are running independently."
      />
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Description</th>
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Severity</th>
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Blocked</th>
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Depends On</th>
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {blockers.map((b) => (
            <tr key={b.id} className="hover:bg-gray-50">
              <td className="px-4 py-2.5 text-xs text-gray-800">{b.description}</td>
              <td className="px-4 py-2.5">
                <Badge variant={severityVariants[b.severity] || 'neutral'}>{b.severity}</Badge>
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{getAgentLabel(b.blockedAgentId)}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">
                {b.crossProjectDependency
                  ? b.crossProjectDependency.description
                  : '--'}
              </td>
              <td className="px-4 py-2.5">
                <span className={`text-[10px] font-medium ${
                  b.status === 'resolved' ? 'text-green-600' :
                  b.status === 'in_progress' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {b.status === 'open' ? 'Open' : b.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
